import type { Command } from 'common/Prose/Commands'
import { schema } from 'prose-core'
import { Schema } from 'prosemirror-model'
import { EditorState, TextSelection } from 'prosemirror-state'
import { canSplit, liftTarget } from 'prosemirror-transform'
import { EditorView } from 'prosemirror-view'
import { setTrackedChangeCommand } from '../Plugins/TrackChanges/commands'
import { CommandName } from '../Plugins/TrackChanges/commands/types'

export const maybeLiftEmptyBlock: Command = (state, dispatch) => {
  if (state.selection.empty) {
    const parentNodeType = state.selection.$from.node(-1)?.type
    // don't break out of a column
    if (parentNodeType === schema.nodes.column) return false
    // don't break out of a box unless the selection is at the end of the box
    const isSelectionAtEndOfParent = state.selection.from === state.selection.$from.end(-1) - 1
    if (parentNodeType === schema.nodes.box && !isSelectionAtEndOfParent) {
      return false
    }
  }
  return liftEmptyBlock(state, dispatch)
}

// NOTICE: Plucked from node_modules/prosemirror-commands/src/commands.js
// :: (EditorState, ?(tr: Transaction)) → bool
// If the cursor is in an empty textblock that can be lifted, lift the
// block.
export function liftEmptyBlock<S extends Schema = any>(state: EditorState<S>, dispatch?: EditorView<S>['dispatch']) {
  const { $cursor } = state.selection instanceof TextSelection ? state.selection : { $cursor: null }
  if (!$cursor || $cursor.parent.content.size) {
    return false
  }
  if ($cursor.depth > 1 && $cursor.after() !== $cursor.end(-1)) {
    const before = $cursor.before()
    if (canSplit(state.doc, before)) {
      if (dispatch) {
        dispatch(state.tr.split(before).scrollIntoView())
      }
      return true
    }
  }
  const range = $cursor.blockRange(),
    target = range && liftTarget(range)
  if (target == null) {
    return false
  }
  if (dispatch && range) {
    const tr = state.tr.lift(range, target).scrollIntoView()
    setTrackedChangeCommand(tr, { name: CommandName.LIFT_EMPTY_BLOCK, meta: { range, target } })
    dispatch(tr)
  }
  return true
}
